import React from 'react';
import {VIDEO_EDITOR_LABELS, VideoEditor} from '../backend-types/user';
import {CheckIcon} from './Icons';

/** The three known editors, in the order the labels map defines them. */
const EDITOR_OPTIONS = Object.entries(VIDEO_EDITOR_LABELS) as [VideoEditor, string][];

interface VideoEditorPickerProps {
  /** `null` means "never asked" — never rendered as a fourth, selected option. */
  value: VideoEditor | null;
  onSelect: (editor: VideoEditor) => void;
  /** Lets a settings save go back to "never asked". Omit to hide the control (onboarding). */
  onClear?: () => void;
  disabled?: boolean;
}

/**
 * Which editor a user imports our XML into — the one preference the convert flow reads to decide
 * how strict to be about the media path. Shared between onboarding and settings so the question is
 * asked the same way in both places; only the surrounding chrome differs per caller.
 *
 * Deliberately a plain click, not a form with a Save button: this is a standing preference, not a
 * draft, so each click is its own write and the picker just reflects whatever the store answers back
 * with.
 */
const VideoEditorPicker: React.FC<VideoEditorPickerProps> = ({value, onSelect, disabled}) => (
  <div>
    <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Video editor">
      {EDITOR_OPTIONS.map(([editor, label]) => {
        const selected = editor === value;
        return (
          <button
            key={editor}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onSelect(editor)}
            className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left
             text-sm transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
          selected
            ? 'border-indigo-500 bg-indigo-500/10 text-indigo-200'
            : 'border-gray-800 bg-gray-950/60 text-gray-300 hover:border-gray-700 hover:bg-gray-800/60'
          }`}
          >
            {label}
            {selected && <CheckIcon className="w-4 h-4 shrink-0 text-indigo-400" />}
          </button>
        );
      })}
    </div>
  </div>
);

export default VideoEditorPicker;
